/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import * as d3 from 'd3';
import React, { useEffect, useId, useRef } from 'react';
import {
  dp,
  maxPressure,
  maxTemperature,
  minPressure,
  minTemperature,
  minEmagramTemperature,
  staticPressureArray,
} from './constants';
import type { Margin } from './drawUtils';
import {
  initializeSvg,
  createScales,
  drawAxes,
  SOUNDING_COLORS,
  plotSounding,
} from './drawUtils';
import { useSharedMouseY } from './SharedMouseYProvider';
import type {
  SoundingDiagramType,
  SoundingFeature,
} from '../../api/edr-api/types';
import {
  constructTooltipData,
  drawSoundingsTooltip,
  extractPressureFromY,
} from '../SoundingsTooltip/utils';
import { drawStaticLines } from '../StaticLines/utils';
import { MAX_VERTICAL_SCALE, MIN_VERTICAL_SCALE } from '../WindBarbs/utils';

const shadow =
  '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 4px 0 rgba(0, 0, 0, 0.12), 0 2px 4px 0 rgba(0, 0, 0, 0.14)';

const soundingsStyle = {
  tooltip: {
    display: 'block',
    position: 'absolute',
    borderRadius: '1.5px',
    backgroundColor: 'geowebColors.soundings.tooltip.background',
    boxShadow: shadow,
    padding: '2px 4px',
    pointerEvents: 'none',
    zIndex: 500,
  },
  pressure: {
    fontSize: '10px',
  },
  temperature: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  dewPoint: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  wind: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
};

export interface SoundingProps {
  d3GraphId: string;
  data: SoundingFeature[][];
  svgDimensions: {
    width: number;
    height: number;
    margin: Margin;
  };
  showTooltip?: boolean;
  diagramType?: SoundingDiagramType;
}

type ZoomableSvgElement = SVGElement & {
  __zoom?: d3.ZoomTransform;
};

const updateTransformToViewport = (
  transform: d3.ZoomTransform,
  width: number,
  height: number,
): d3.ZoomTransform => {
  const minX = width * (1 - transform.k);
  const minY = height * (1 - transform.k);

  return d3.zoomIdentity
    .translate(
      Math.min(0, Math.max(minX, transform.x)),
      Math.min(0, Math.max(minY, transform.y)),
    )
    .scale(transform.k);
};

export const Soundings: React.FC<SoundingProps> = ({
  d3GraphId,
  data,
  svgDimensions,
  showTooltip = true,
  diagramType = 'SKEWT',
}) => {
  const instanceId = useId().replace(/:/g, '_');
  const scopedGraphId = `${d3GraphId}-${instanceId}`;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const pressureRef = useRef<HTMLDivElement | null>(null);
  const tempRef = useRef<HTMLDivElement | null>(null);
  const dewpointRef = useRef<HTMLDivElement | null>(null);

  // Refs to store current scale/data for shared mouse effect
  const yScaleRef = useRef<d3.ScaleLinear<number, number> | null>(null);
  const marginRef = useRef<Margin | null>(null);
  const widthRef = useRef<number>(0);
  const svgRectRef = useRef<DOMRect | null>(null);
  const tooltipDataRef = useRef<ReturnType<typeof constructTooltipData> | null>(
    null,
  );
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGElement, unknown> | null>(
    null,
  );
  // Keep zoom target on the inner translated <g>; Soundings and WindBarbs must
  // transform the same coordinate space to stay aligned.
  const zoomTargetRef = useRef<SVGElement | null>(null);
  const isApplyingSharedZoomRef = useRef(false);
  // Keep the last applied d3 zoom transform across SVG redraws.
  // Width/height changes recreate the chart, so this ref prevents zoom snapping
  // back to identity when panels are closed/resized.
  const persistedZoomTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const previousDiagramTypeRef = useRef<SoundingDiagramType | null>(null);

  const { clientY, publishVerticalZoom, verticalZoom, resetVerticalZoom } =
    useSharedMouseY();

  // Set SVG dimensions (required props; no optional chaining needed)
  const { width: baseWidth, height: baseHeight, margin } = svgDimensions;
  const width = baseWidth - (margin.left + margin.right);
  const height = baseHeight - (margin.top + margin.bottom);

  useEffect(() => {
    if (!svgRef.current || width <= 0 || height <= 0) {
      return;
    }

    const isDiagramTypeChange =
      previousDiagramTypeRef.current !== null &&
      previousDiagramTypeRef.current !== diagramType;

    if (isDiagramTypeChange) {
      persistedZoomTransformRef.current = d3.zoomIdentity;
      resetVerticalZoom(scopedGraphId);
    }

    const root = d3.select(svgRef.current);
    root.selectAll('*').remove();

    const svg = initializeSvg({
      svgRef,
      width,
      height,
      margin,
      diagramType,
      d3GraphId: scopedGraphId,
    }) as unknown as d3.Selection<SVGElement, unknown, HTMLElement, unknown>;

    // Create a separate layer for axes so they can stay visible above the zoomed content.
    const axisLayer = svg
      .append('g')
      .attr('data-layer', 'axis-layer') as unknown as d3.Selection<
      SVGElement,
      unknown,
      HTMLElement,
      unknown
    >;

    // Keep a static viewport group for clipping, and transform only the inner
    // content group during zoom so the visible area stays fixed.
    const zoomViewport = svg
      .append('g')
      .attr('data-layer', 'zoom-viewport')
      .attr('clip-path', `url(#clip-${scopedGraphId}-${diagramType}d3)`);

    const zoomContent = zoomViewport
      .append('g')
      .attr('data-layer', 'zoom-content') as unknown as d3.Selection<
      SVGElement,
      unknown,
      HTMLElement,
      unknown
    >;

    const bijvoetLabelLayer = svg
      .append('g')
      .attr('data-layer', 'bijvoet-label-layer')
      .style('pointer-events', 'none') as unknown as d3.Selection<
      SVGElement,
      unknown,
      HTMLElement,
      unknown
    >;

    const svgRectangle = svgRef.current.getBoundingClientRect();
    svgRectRef.current = svgRectangle;
    marginRef.current = margin;
    widthRef.current = width;

    const minT =
      diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;
    const maxT = maxTemperature;

    const [xScale, yScale] = createScales(
      diagramType,
      width,
      height,
      minPressure,
      maxPressure,
      minT,
      maxT,
    );
    yScaleRef.current = yScale;

    const applyAxisClips = (): void => {
      axisLayer
        .select('g[type="x-axis"] path.domain')
        .attr('d', `M0.5,0.5H${width + 0.5}`);
      axisLayer
        .select('g[type="x-axis"]')
        .attr('clip-path', `url(#clip-${scopedGraphId}-${diagramType}x-axis)`);
      axisLayer
        .select('g[type="y-axis"]')
        .attr('clip-path', `url(#clip-${scopedGraphId}-${diagramType}y-axis)`);
    };

    drawAxes({ svg: axisLayer, xScale, yScale, height, diagramType });
    applyAxisClips();

    drawStaticLines({
      diagramType,
      dp,
      staticPressureArray,
      minTemperature: minT,
      maxTemperature: maxT,
      height,
      width,
      xScale,
      yScale,
      minPressure,
      maxPressure,
      svg: zoomContent,
      bijvoetLabelSvg: bijvoetLabelLayer,
      d3GraphId: scopedGraphId,
    });

    data.forEach((features, index) => {
      const color = SOUNDING_COLORS[index] || SOUNDING_COLORS[0];
      const temperatureValues = features.map(
        (feature) => feature.properties.temp,
      );
      const pressureValues = features.map(
        (feature) => feature.properties.pressure,
      );
      plotSounding({
        temperatureValues,
        pressureValues,
        color,
        lineStyle: 'none',
        d3GraphId: scopedGraphId,
        xScale,
        yScale,
        height,
        svg: zoomContent,
        diagramType,
      });
      const dewpointValues = features.map(
        (feature) => feature.properties.dewpoint,
      );
      plotSounding({
        temperatureValues: dewpointValues,
        pressureValues,
        color,
        lineStyle: '8, 2',
        d3GraphId: scopedGraphId,
        xScale,
        yScale,
        height,
        svg: zoomContent,
        diagramType,
      });
    });

    tooltipDataRef.current = constructTooltipData(data);

    const zoomTarget = zoomContent.node();
    if (!zoomTarget) {
      return;
    }
    zoomTargetRef.current = zoomTarget;

    const bijvoetLabelTarget = bijvoetLabelLayer.node();
    if (!bijvoetLabelTarget) {
      return;
    }

    const applyZoomTransform = (transform: d3.ZoomTransform): void => {
      d3.select(zoomTarget).attr('transform', transform.toString());
      d3.select(bijvoetLabelTarget).attr('transform', transform.toString());
    };

    // Allow zooming and panning
    const zoomBehavior = d3
      .zoom<SVGElement, unknown>()
      .scaleExtent([MIN_VERTICAL_SCALE, MAX_VERTICAL_SCALE])
      .extent([
        [0, 0],
        [width, height],
      ])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on('zoom', (event: d3.D3ZoomEvent<SVGElement, unknown>) => {
        // Persist every interactive zoom/pan event so the transform can be
        // restored after dimension-driven rerenders.
        persistedZoomTransformRef.current = event.transform;
        applyZoomTransform(event.transform);

        const zoomedXScale = event.transform.rescaleX(xScale);
        const zoomedYScale = event.transform.rescaleY(yScale);
        const axisXScale =
          diagramType === 'SKEWT'
            ? xScale.copy().range(
                xScale.range().map((xValue) => {
                  // Skew-T X uses x + (height - y). To keep tick labels tied to
                  // isotherm intersections on the bottom axis, apply the same
                  // transformed bottom-edge offset to the axis scale.
                  const skewBottomOffset =
                    (event.transform.k - 1) * height + event.transform.y;
                  return (
                    event.transform.k * xValue +
                    event.transform.x +
                    skewBottomOffset
                  );
                }),
              )
            : zoomedXScale;
        const axisYScale =
          diagramType === 'BIJVOET'
            ? yScale
                .copy()
                .range(
                  yScale
                    .range()
                    .map(
                      (yValue) =>
                        event.transform.k * yValue + event.transform.y,
                    ),
                )
            : zoomedYScale;
        axisLayer.selectAll('*').remove();
        drawAxes({
          svg: axisLayer,
          xScale: axisXScale,
          yScale: axisYScale,
          height,
          diagramType,
        });
        applyAxisClips();

        if (!isApplyingSharedZoomRef.current) {
          publishVerticalZoom(
            scopedGraphId,
            event.transform.k,
            event.transform.y,
          );
        }
      });
    zoomBehaviorRef.current = zoomBehavior;
    svg.call(zoomBehavior);

    const previousTransform = updateTransformToViewport(
      persistedZoomTransformRef.current,
      width,
      height,
    );
    const hasPersistedTransform =
      previousTransform.k > 1 ||
      Math.abs(previousTransform.x) > 1e-3 ||
      Math.abs(previousTransform.y) > 1e-3;

    // Re-apply the previous transform after rebuilding the SVG so resize
    // operations preserve the current zoom level/pan offset.
    if (hasPersistedTransform) {
      isApplyingSharedZoomRef.current = true;
      svg.call(zoomBehavior.transform, previousTransform);
      isApplyingSharedZoomRef.current = false;
    }

    // Keep shared zoom state aligned with the effective transform that was
    // restored after resize so companion panels (WindBarbs) do not wait for the
    // next interactive zoom event.
    publishVerticalZoom(
      scopedGraphId,
      previousTransform.k,
      previousTransform.y,
    );

    previousDiagramTypeRef.current = diagramType;

    // eslint-disable-next-line consistent-return
    return (): void => {
      // Read the transform directly from d3's internal zoom state before this
      // chart instance is torn down, then reuse it on the next mount pass.
      const zoomState = (zoomTarget as ZoomableSvgElement).__zoom;
      if (zoomState) {
        persistedZoomTransformRef.current = zoomState;
      }
      svg.on('zoom', null);
      zoomBehaviorRef.current = null;
      zoomTargetRef.current = null;
      root.selectAll('*').remove();
    };
  }, [
    data,
    diagramType,
    width,
    height,
    scopedGraphId,
    margin,
    publishVerticalZoom,
    resetVerticalZoom,
  ]);

  useEffect(() => {
    if (!showTooltip || !svgRef.current) {
      pressureRef.current && (pressureRef.current.style.display = 'none');
      tempRef.current && (tempRef.current.style.display = 'none');
      dewpointRef.current && (dewpointRef.current.style.display = 'none');
      return;
    }

    const rect = svgRectRef.current ?? svgRef.current.getBoundingClientRect();
    if (clientY == null || clientY < rect.top || clientY > rect.bottom) {
      pressureRef.current && (pressureRef.current.style.display = 'none');
      tempRef.current && (tempRef.current.style.display = 'none');
      dewpointRef.current && (dewpointRef.current.style.display = 'none');
      return;
    }

    if (!yScaleRef.current || !marginRef.current || !tooltipDataRef.current) {
      return;
    }

    // Synthetic event with shared clientY
    const synthetic = {
      clientX: rect.left + rect.width / 2,
      clientY,
      pageY: clientY + window.scrollY,
      target: svgRef.current,
      currentTarget: svgRef.current,
    } as unknown as MouseEvent;

    const pressureRefd3 = d3.select(pressureRef.current);
    const tempRefd3 = d3.select(tempRef.current);
    const dewpointRefd3 = d3.select(dewpointRef.current);

    const minT =
      diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;
    const maxT = maxTemperature;
    const [, yScale] = createScales(
      diagramType,
      width,
      height,
      minPressure,
      maxPressure,
      minT,
      maxT,
    );

    const pointerY = clientY - rect.top - marginRef.current.top;
    const unscaledY = (pointerY - verticalZoom.y) / verticalZoom.scale;
    const hoveredPressure = extractPressureFromY(
      diagramType,
      unscaledY,
      yScale,
    );

    drawSoundingsTooltip({
      event: synthetic,
      svgRectangle: rect,
      margin: marginRef.current,
      width: widthRef.current,
      hoveredPressure,
      pressureRefd3,
      tempRefd3,
      dewpointRefd3,
      tooltipData: tooltipDataRef.current,
      diagramType,
    });
  }, [
    clientY,
    showTooltip,
    diagramType,
    data,
    width,
    height,
    verticalZoom.scale,
    verticalZoom.y,
  ]);

  return (
    <Box ref={canvasRef} sx={{ position: 'relative' }}>
      <svg ref={svgRef} />
      <Box
        data-testid="pressure-tooltip"
        ref={pressureRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.pressure,
        }}
      />
      <Box
        data-testid="temperature-tooltip"
        ref={tempRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.temperature,
        }}
      />
      <Box
        data-testid="dewpoint-tooltip"
        ref={dewpointRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.dewPoint,
        }}
      />
    </Box>
  );
};
